import React from 'react';
import { CheckIcon, ServerIcon, HashIcon, GlobeIcon, ActivityIcon, ShieldCheckIcon, DatabaseIcon, CpuIcon } from 'lucide-react';
import { Invoice, Checkpoint } from '../../lib/thetape-data';
interface VerificationDetailProps {
  invoice: Invoice;
}
export function VerificationDetail({
  invoice
}: VerificationDetailProps) {
  const formatCurrency = (val: number) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(val);
  const getTypeIcon = (type: Checkpoint['type']) => {
    switch (type) {
      case 'erp':
        return <DatabaseIcon size={14} className="text-slate-500" data-id="element-383" />;
      case 'explicit':
        return <CheckIcon size={14} className="text-slate-500" data-id="element-384" />;
      case 'hash':
        return <HashIcon size={14} className="text-slate-500" data-id="element-385" />;
      case 'api':
        return <GlobeIcon size={14} className="text-slate-500" data-id="element-386" />;
      case 'graph':
        return <ActivityIcon size={14} className="text-slate-500" data-id="element-387" />;
      case 'ai':
        return <CpuIcon size={14} className="text-slate-500" data-id="element-388" />;
      case 'learn':
        return <ServerIcon size={14} className="text-slate-500" data-id="element-389" />;
      default:
        return <CheckIcon size={14} className="text-slate-500" data-id="element-390" />;
    }
  };
  const getTypeLabel = (type: Checkpoint['type']) => {
    return type.toUpperCase();
  };
  return <div className="animate-in fade-in slide-in-from-bottom-4 duration-300" data-id="element-391">
      {/* Invoice Header Card */}
      <div className="bg-slate-900 border border-slate-800 p-6 mb-6" data-id="element-392">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6" data-id="element-393">
          <div data-id="element-394">
            <div className="flex items-center gap-3 mb-2" data-id="element-395">
              <h2 className="text-2xl font-mono text-white font-semibold tracking-tight" data-id="element-396">
                {invoice.id}
              </h2>
              <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-500 text-xs font-mono border border-emerald-500/20 rounded" data-id="element-397">
                13/13 VERIFIED
              </span>
            </div>
            <div className="flex gap-8 text-sm text-slate-400 font-mono" data-id="element-398">
              <div data-id="element-399">
                <span className="block text-xs text-slate-500 mb-1" data-id="element-400">
                  OBLIGOR
                </span>
                <span className="text-slate-200" data-id="element-401">{invoice.obligor}</span>
                <span className="ml-2 text-xs px-1.5 py-0.5 bg-slate-800 rounded text-slate-400" data-id="element-402">
                  Rating: {invoice.creditRating}
                </span>
              </div>
              <div data-id="element-403">
                <span className="block text-xs text-slate-500 mb-1" data-id="element-404">
                  SUPPLIER
                </span>
                <span className="text-slate-200" data-id="element-405">{invoice.supplier}</span>
                <span className="ml-2 text-xs text-slate-500" data-id="element-406">Anonymized</span>
              </div>
            </div>
          </div>

          <div className="bg-slate-950 px-6 py-4 border border-slate-800 min-w-[240px]" data-id="element-407">
            <span className="block text-xs text-slate-500 font-mono mb-1" data-id="element-408">
              TOTAL DUE
            </span>
            <span className="text-3xl font-mono text-white tracking-tight" data-id="element-409">
              {formatCurrency(invoice.amount)}
            </span>
          </div>
        </div>

        {/* Invoice Meta Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-slate-800" data-id="element-410">
          <div data-id="element-411">
            <span className="block text-xs text-slate-500 font-mono mb-1" data-id="element-412">
              ISSUE DATE
            </span>
            <span className="text-sm text-slate-300 font-mono" data-id="element-413">
              {invoice.issueDate}
            </span>
          </div>
          <div data-id="element-414">
            <span className="block text-xs text-slate-500 font-mono mb-1" data-id="element-415">
              DUE DATE
            </span>
            <span className="text-sm text-slate-300 font-mono" data-id="element-416">
              {invoice.dueDate}
            </span>
          </div>
          <div data-id="element-417">
            <span className="block text-xs text-slate-500 font-mono mb-1" data-id="element-418">
              TERMS
            </span>
            <span className="text-sm text-slate-300 font-mono" data-id="element-419">
              Net {invoice.tenorDays}
            </span>
          </div>
          <div data-id="element-420">
            <span className="block text-xs text-slate-500 font-mono mb-1" data-id="element-421">
              PO REFERENCE
            </span>
            <span className="text-sm text-slate-300 font-mono" data-id="element-422">
              {invoice.poNumber}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-423">
        {/* Retailer Verification Column */}
        <div className="space-y-3" data-id="element-424">
          <h3 className="text-xs font-mono text-slate-500 uppercase tracking-wider mb-4 pl-1" data-id="element-425">
            Retailer Verification (01-06)
          </h3>
          {invoice.checkpoints.slice(0, 6).map(cp => <div key={cp.id} className="group flex items-stretch bg-slate-900 border border-slate-800 hover:border-slate-700 transition-colors" data-id="element-426">
              <div className="w-12 flex items-center justify-center border-r border-slate-800 bg-slate-950/50 font-mono text-slate-500 text-sm" data-id="element-427">
                {cp.id}
              </div>
              <div className="flex-1 p-3" data-id="element-428">
                <div className="flex justify-between items-start mb-1" data-id="element-429">
                  <span className="text-sm font-medium text-slate-200" data-id="element-430">
                    {cp.name}
                  </span>
                  <div className="flex items-center gap-2" data-id="element-431">
                    <span className="text-[10px] font-mono text-slate-600 px-1.5 py-0.5 bg-slate-950 rounded border border-slate-800" data-id="element-432">
                      {getTypeLabel(cp.type)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2" data-id="element-433">
                  <CheckIcon size={14} className="text-emerald-500" data-id="element-434" />
                  <span className="text-xs text-emerald-500 font-mono" data-id="element-435">
                    {cp.details}
                  </span>
                </div>
              </div>
              <div className="w-24 border-l border-slate-800 flex items-center justify-center bg-slate-950/30" data-id="element-436">
                <span className="text-[10px] font-mono text-slate-500" data-id="element-437">
                  {cp.timestamp.split(' ')[1]}
                </span>
              </div>
            </div>)}
        </div>

        {/* Supplier Verification Column */}
        <div className="space-y-3" data-id="element-438">
          <h3 className="text-xs font-mono text-slate-500 uppercase tracking-wider mb-4 pl-1" data-id="element-439">
            Supplier Verification (07-13)
          </h3>
          {invoice.checkpoints.slice(6).map(cp => <div key={cp.id} className="group flex items-stretch bg-slate-900 border border-slate-800 hover:border-slate-700 transition-colors" data-id="element-440">
              <div className="w-12 flex items-center justify-center border-r border-slate-800 bg-slate-950/50 font-mono text-slate-500 text-sm" data-id="element-441">
                {cp.id}
              </div>
              <div className="flex-1 p-3" data-id="element-442">
                <div className="flex justify-between items-start mb-1" data-id="element-443">
                  <span className="text-sm font-medium text-slate-200" data-id="element-444">
                    {cp.name}
                  </span>
                  <div className="flex items-center gap-2" data-id="element-445">
                    <span className="text-[10px] font-mono text-slate-600 px-1.5 py-0.5 bg-slate-950 rounded border border-slate-800" data-id="element-446">
                      {getTypeLabel(cp.type)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2" data-id="element-447">
                  <CheckIcon size={14} className="text-emerald-500" data-id="element-448" />
                  <span className="text-xs text-emerald-500 font-mono" data-id="element-449">
                    {cp.details}
                  </span>
                </div>
              </div>
              <div className="w-24 border-l border-slate-800 flex items-center justify-center bg-slate-950/30" data-id="element-450">
                <span className="text-[10px] font-mono text-slate-500" data-id="element-451">
                  {cp.timestamp.split(' ')[1]}
                </span>
              </div>
            </div>)}
        </div>
      </div>

      {/* Footer Summary */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-452">
        <div className="bg-slate-950 border border-slate-800 p-4 text-center" data-id="element-453">
          <span className="block text-xs text-slate-500 font-mono mb-1" data-id="element-454">
            VERIFICATION CHECKPOINTS
          </span>
          <span className="text-2xl font-mono text-white" data-id="element-455">13</span>
        </div>
        <div className="bg-slate-950 border border-slate-800 p-4 text-center" data-id="element-456">
          <span className="block text-xs text-slate-500 font-mono mb-1" data-id="element-457">
            PASS RATE REQUIRED
          </span>
          <span className="text-2xl font-mono text-emerald-500" data-id="element-458">100%</span>
        </div>
        <div className="bg-slate-950 border border-slate-800 p-4 text-center" data-id="element-459">
          <span className="block text-xs text-slate-500 font-mono mb-1" data-id="element-460">
            OBLIGOR CREDIT
          </span>
          <span className="text-2xl font-mono text-white" data-id="element-461">
            {invoice.creditRating}
          </span>
        </div>
        <div className="bg-slate-950 border border-slate-800 p-4 text-center" data-id="element-462">
          <span className="block text-xs text-slate-500 font-mono mb-1" data-id="element-463">
            DEFAULT RATE
          </span>
          <span className="text-2xl font-mono text-emerald-500" data-id="element-464">&lt;0.5%</span>
        </div>
      </div>
    </div>;
}